import { MimeType } from '../../../common/media/mimeConstants';
import getFilenameFromUrl from '../../../common/files/getFilenameFromUrl';
import { APP_MEDIA_PATH_PREFIX, APP_QUERY_PARAMS, MEDIA_CDN_HOSTNAME } from '../../../common/utils/urlConstants';
import { getIsMilanoteDomain, isAlternateWebHost, parseUrl } from '../../../common/utils/urlUtil';

const WEBP_FILE_EXTENSION = 'webp';

/**
 * App-hosted v2 media URLs are permission-checked by the media edge Lambda, which authorises from the
 * request's cookies (the legacy `mn-token`, or the Better Auth session cookie). Every other media URL
 * — third-party, CDN-hosted, or app-hosted without `v=2` — is passed straight through unchecked and so
 * loads anonymously.
 *
 * Callers use this to decide whether a request has to carry credentials.
 */
export const isPermissionCheckedAppMediaUrl = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    if (!parsedUrl) return false;

    // A third-party host is never permission-checked, and would reject the credentialed request the
    // callers switch to, so the host has to be one this environment serves app media from.
    const isAppHostedUrl =
        (getIsMilanoteDomain(url) || isAlternateWebHost(url)) && parsedUrl.hostname !== MEDIA_CDN_HOSTNAME;
    if (!isAppHostedUrl || !parsedUrl.pathname.startsWith(APP_MEDIA_PATH_PREFIX)) return false;

    return (
        parsedUrl.searchParams.get(APP_QUERY_PARAMS.VERSION) === '2' &&
        !!parsedUrl.searchParams.get(APP_QUERY_PARAMS.ELEMENT_ID)
    );
};

/**
 * Infer MIME type from the image URL filename when the blob type is unavailable.
 */
export const getMimeTypeFromImageUrl = (imageUrl: string | null, fallbackMimeType: string): string => {
    if (!imageUrl) return fallbackMimeType;

    const filename = getFilenameFromUrl(imageUrl, fallbackMimeType).toLowerCase();
    if (filename.endsWith(`.${WEBP_FILE_EXTENSION}`)) return MimeType.WEBP;
    if (filename.endsWith('.png')) return MimeType.PNG;

    return fallbackMimeType;
};
